import { v4 } from "uuid";
import { clientCache } from "../pages/shared/components/Editors";
import {
  formatIntizaDate,
  formatDateDigits,
  formatInteger,
  formatNumber
} from "../utils/FormatUtils";
import { parseDate, parseIntOrDefault } from "../utils/ParseUtils";
import { OptionalMap, isNullOrWhitespace } from "../utils/Utils";
import ActivityService from "./ActivityService";
import CompanyService, { DataType, Entities } from "./CompanyService";
import {
  FilterCollection,
  FilterDefinition,
  FilterOption
} from "./FilterService";
import { TranslationService } from "./TranslationService";
import moment from "moment";
import { CacheEntity, StorageService } from "./StorageService";
import { Group } from "../pages/task/entities/TaskGetResponse";

/**
 * Mapeos de compatibilidad entre versiones
 *
 * NOTA: Según el enum del backend (filterFields):
 * - groupId = -1007 (usado para "Asegurado" - CompanyService.getGroupName())
 * - groupName = -1008 (usado para el grupo estándar - TranslationService.translate.Group)
 *
 * "Asegurado" debe usar -1007 (groupId) al setearse/guardarse, según el enum del backend.
 *
 * V1 → V2: Para parsear filtros antiguos que puedan tener -1008 cuando deberían tener -1007
 * V2 → V1: Ya no es necesario porque ahora usamos -1007 directamente en las definiciones
 */
const FIELD_MAPPING_V1_TO_V2: Record<string, string> = {
  "-1008": "-1007", // groupName (antiguo) -> groupId (correcto) - Por si hay filtros antiguos con -1008 que deberían ser -1007
  "-110": "-7" // Activities.fields.segments (V1) -> Persons.filterFields.Segment (V2) - Reportes programados creados desde V1
};

// V1 (Intiza antiguo) usa "personadditional-X" para campos adicionales de clientes; V2 usa "additional-X"
const V1_PERSON_ADDITIONAL_PREFIX = "personadditional-";
const V2_ADDITIONAL_PREFIX = "additional-";

class AdvancedFilterService {
  public static filterToString(
    filter: Filter,
    options?: { useEntityPrefix?: boolean }
  ) {
    const requestParams: FilterQueryParam = {
      text: "",
      date: "",
      amount: "",
      selYesNo: "0",
      selStatus: "0",
      operatorContains: "3",
      selOperatorDate: "0",
      selOperator: "0",
      operatorList: "0",
      selList: ""
    };
    switch (filter.kind) {
      case DataType.Text:
      case DataType.Phone:
      case DataType.Link:
      case DataType.TextWithOperator:
        requestParams.text = filter.value;
        requestParams.operatorContains = (filter.operator ?? 0)?.toString();
        break;
      case DataType.List:
        requestParams.selList =
          filter.value ??
          filter.definition?.AdditionalDefinitionItems?.[0]?.Value ??
          filter.value;
        // Normalize duplicated operator label for List ("EqualTo" at indexes 0 and 5)
        const normalizedOperator =
          filter.operator === 5 ? 0 : (filter.operator ?? 0);
        requestParams.operatorList = normalizedOperator.toString();
        if (filter.definition.Field === "transactiontype") {
          requestParams.transactiontype =
            filter.value ??
            filter.definition?.AdditionalDefinitionItems?.[0]?.Value ??
            filter.value;
          requestParams.operatortransactiontypelist =
            normalizedOperator.toString();
        }
        break;
      case DataType.ListNoOperator:
        requestParams.selCashflow =
          filter.value ??
          filter.definition?.AdditionalDefinitionItems?.[0]?.Value ??
          filter.value;
        break;
      case DataType.Date:
        // Days and absolute dates are mutually exclusive; days win when present.
        if (!isNullOrWhitespace(filter.relativeDays)) {
          requestParams.dateActivityFilter = filter.relativeDays;
          requestParams.selOperatorDate = (filter.operator ?? 0).toString();
        } else {
          requestParams.selDate = filter.value ?? formatIntizaDate(new Date());
        }
        break;
      case DataType.Number:
      case DataType.Currency:
      case DataType.DaysOld:
        requestParams.amount = filter.value;
        requestParams.selOperator = (filter.operator ?? 0)?.toString();
        break;
      case DataType.DayCompare:
        requestParams.date = filter.value;
        requestParams.selOperatorDate = (filter.operator ?? 0)?.toString();
        //&filter0=text%3d%26date%3d6%26amount%3d%26selFields%3d-1023%26selYesNo%3d0%26selStatus%3d0%26operatorContains%3d3%26selOperatorDate%3d2%26selOperator%3d0%26operatorList%3d0%26selList%3dActualizando%2blista...&filter1=text%3d%26date%3d%26amount%3d%26selFields%3d%26selYesNo%3d0%26selStatus%3d0%26operatorContains%3d3%26selOperatorDate%3d0%26selOperator%3d0%26operatorList%3d0%26selList%3dActualizando%2blista...
        //TODO: COMPLETAR SEL OPERATOR
        break;
      case DataType.Percentage:
        requestParams.percent = filter.value;
        requestParams.selOperatorPercent = (filter.operator ?? 0)?.toString();
        break;
      case DataType.Tags:
        requestParams.tagidsFilter = filter.value?.replaceAll(" ", "");
        break;
      case DataType.Multiselect:
        if (!filter.value) return "";
        requestParams.selList = filter.value;
        requestParams.operatorList = "0";
        break;
      case DataType.ActivityType:
        requestParams.selActivityType = filter.value;
        requestParams.activitytypeFilter = filter.value;
        requestParams.operatorContains = "3";
        break;
      case DataType.Client:
        requestParams.personidFilter = filter.value;
        break;
      case DataType.Priority:
        // Multiple selection: travels like Multiselect (selList with comma-separated values)
        requestParams.selList = filter.value?.replaceAll(" ", "") || "1";
        requestParams.operatorList = "0";
        break;
      case DataType.ExpirationDate:
        requestParams.selfilter = filter.value ?? "all";
        break;
      case DataType.ClientGroup:
        requestParams.personidFilter = filter.value;
        requestParams.groupFilterid = filter.operator
          ? filter.operator.toString()
          : undefined;
        break;
      case DataType.YesNo:
        requestParams.selYesNo = filter.value;
        break;
      case DataType.User:
        requestParams.useridFilter = filter.value;
        break;
      case DataType.UserMultiSelect:
        requestParams.useridFilter = filter.value;
        break;
      case DataType.Type:
        requestParams.typeFilter = filter.value;
        break;
      case DataType.Status:
        requestParams.selStatus = filter.value ?? "0";
        break;
      case DataType.ActivityTypeWithDate:
        {
          const activityFilter = filter as FilterActivityTypeWithDate;
          // Days and absolute dates are mutually exclusive; days win when present.
          if (!isNullOrWhitespace(filter.relativeDays)) {
            requestParams.dateActivityFilter = filter.relativeDays;
            requestParams.selOperatorDate = (filter.operator ?? 0).toString();
          } else {
            requestParams.selDateActivityType = filter.value;
          }
          requestParams.selActivityType =
            activityFilter?.activityTypeFilter ?? "";
          requestParams.selActivityUser = activityFilter?.userId ?? "";
        }
        break;
      default:
        throw Error("Not implemented filter: " + JSON.stringify(filter));
    }
    requestParams.selFields = options?.useEntityPrefix
      ? AdvancedFilterService.mapFieldV2ToV1(filter.definition.Field)
      : filter.definition.Field;
    // Construir query string manualmente para evitar que URLSearchParams codifique espacios como +
    // Codificamos cada valor para manejar correctamente caracteres especiales como &
    const queryParts: string[] = [];
    Object.keys(requestParams).forEach((key) => {
      const value = Reflect.get(requestParams, key);
      if (value !== undefined && value !== null) {
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    });
    return queryParts.join("&");
  }

  public static parseFilters(
    text: string | undefined,
    options: FilterOption[]
  ): FilterLine[] {
    if (!text) {
      return [];
    }
    const filters: FilterLine[] = [];
    const cleanText = text.trim().endsWith("&")
      ? text.trim().slice(0, -1)
      : text.trim();
    const parts = cleanText.split("&");

    // Agrupar partes por marcadores filterN= para soportar dos formatos:
    // - V2 (actual): filter0=<inner_codificado>&filter1=<inner_codificado>
    //   El inner filter está URL-encoded completo en el valor de filterN.
    // - V1 (Intiza antiguo): filter0=primerParam&param2&param3&filter1=...
    //   Los parámetros del inner filter NO están codificados; quedan como params
    //   separados por & a nivel del outer string.
    type FilterGroup = { key: string; parts: string[] };
    const filterGroups: FilterGroup[] = [];
    let currentGroup: FilterGroup | null = null;

    for (const part of parts) {
      if (!part || part.trim().length === 0) continue;
      const equalIndex = part.indexOf("=");
      if (equalIndex === -1) continue;
      const partKey = part.substring(0, equalIndex);

      if (partKey.match(/^filter\d+$/i)) {
        if (currentGroup) {
          filterGroups.push(currentGroup);
        }
        currentGroup = {
          key: partKey,
          parts: [part.substring(equalIndex + 1)]
        };
      } else if (currentGroup) {
        currentGroup.parts.push(part);
      }
    }
    if (currentGroup) {
      filterGroups.push(currentGroup);
    }

    for (const group of filterGroups) {
      const firstValue = group.parts[0];
      if (!firstValue || firstValue.trim().length === 0) continue;

      let decodedFirst: string;
      try {
        decodedFirst = decodeURIComponent(firstValue);
      } catch {
        decodedFirst = firstValue;
      }

      // Detectar el formato por si el valor decodificado contiene "&":
      // - Si contiene "&" → es formato V2: el inner filter está completo y codificado
      // - Si no contiene "&" → es formato V1: los params del inner filter están en partes separadas
      let encodedFilter: string;
      if (decodedFirst.includes("&")) {
        encodedFilter = decodedFirst;
      } else {
        encodedFilter = group.parts.join("&");
      }

      try {
        const filter = FilterLine.From(encodedFilter, options);
        if (!filter.isEmpty()) {
          filters.push(filter);
        } else {
          console.warn(
            `Filter ${group.key} parsed but is empty. Encoded filter: ${encodedFilter.substring(0, 100)}...`
          );
        }
      } catch (error) {
        console.warn(
          `Error parsing filter ${group.key}:`,
          error,
          "Encoded filter:",
          encodedFilter.substring(0, 100)
        );
      }
    }
    return filters;
  }

  public static FriendlyName(filter: Filter | undefined): string | undefined {
    if (!filter) {
      return undefined;
    }
    let friendlyName = filter.definition.Name + " - ";
    filter.value = filter?.value ?? "";
    try {
      const decodedValue = decodeURIComponent(filter.value);
      filter.value = decodedValue;
    } catch {}

    switch (filter?.kind) {
      case DataType.Text:
      case DataType.Phone:
      case DataType.Link:
      case DataType.TextWithOperator:
        friendlyName += TranslationService.getTranslation(
          AdvancedFilterService.textWithOperatorOperators[filter.operator ?? 0]
        );
        friendlyName = appendFriendly(friendlyName, filter.value);
        break;
      case DataType.List:
        {
          const operatorValue = filter.operator ?? 0;

          // Manejar operador Dynamic (valor 9) específicamente
          // El operador Dynamic (9) no está en listOperators, por lo que necesitamos manejarlo específicamente

          if (operatorValue === 9) {
            // Para operador 9, siempre usar la traducción de Dynamic (usado en super segmentos)
            // El operador 9 puede ser tanto Dynamic como Includes, pero en el contexto de filtros
            // de segmentos con campos de tipo lista, si es 9 y el valor está vacío, es Dynamic
            // Usar getTranslation con la clave "Dynamic" o acceder directamente a translate.Dynamic como fallback
            const dynamicTranslation =
              TranslationService.getTranslation("Dynamic") ||
              TranslationService.translate.Dynamic ||
              "Dinámico";
            friendlyName += dynamicTranslation;
            // Dynamic no muestra valores seleccionados, así que terminamos aquí
          } else {
            // Usar el array estático de operadores para otros valores (0-7)
            const operatorKey =
              AdvancedFilterService.listOperators[operatorValue];
            if (operatorKey) {
              friendlyName += TranslationService.getTranslation(operatorKey);
            }

            // Mostrar valores seleccionados
            const values = filter.value
              ?.split(",")
              .map((v) => v.trim())
              .filter((v) => v.length > 0);

            // Para segmentos (Field === "-7" o -7), cargar dinámicamente desde CompanyService
            let items: { Label: string; Value: string }[] = [];
            const isSegmentField =
              filter.definition.Field === "-7" ||
              filter.definition.Field === -7 ||
              String(filter.definition.Field) === "-7";
            if (isSegmentField) {
              // Cargar segmentos dinámicamente
              const segments = CompanyService.getSegments();

              // Convertir values a string para comparación robusta
              const valuesAsStrings = values?.map((v) => String(v)) ?? [];
              // const segmentIdsAsStrings = segments.map(s => String(s.Id));

              items = segments
                .filter((segment) => {
                  const segmentIdStr = String(segment.Id);
                  const found = valuesAsStrings.includes(segmentIdStr);
                  return found;
                })
                .map((segment) => ({
                  Label: segment.Value,
                  Value: String(segment.Id)
                }));
            } else {
              // Para otros tipos de lista, usar AdditionalDefinitionItems
              items = filter.definition.AdditionalDefinitionItems.filter((x) =>
                values?.includes(x.Value)
              );
            }

            const value = items.map((x) => x.Label).join(", ");
            friendlyName = appendFriendly(friendlyName, value);
          }
        }
        break;
      case DataType.ListNoOperator:
        {
          // Attempt to find the label for the current value. Fallback to first item, if value not found.
          const item =
            filter.definition.AdditionalDefinitionItems.find(
              (x) => x.Value === filter.value
            ) ?? filter.definition.AdditionalDefinitionItems[0];
          friendlyName += item?.Label ?? "";
        }
        break;
      case DataType.Date:
        friendlyName += isNullOrWhitespace(filter.relativeDays)
          ? formatDateFilter(filter)
          : formatRelativeDaysFilter(filter);
        break;
      case DataType.PeriodoGestiones:
        friendlyName += isNullOrWhitespace(filter.relativeDays)
          ? formatDateFilter(filter)
          : formatRelativeDaysFilter(filter);
        break;
      case DataType.Number:
      case DataType.Currency:
      case DataType.DaysOld:
        // eslint-disable-next-line no-lone-blocks
        {
          friendlyName +=
            TranslationService.getTranslation(
              AdvancedFilterService.numericOperator[filter.operator ?? 0]
            ) + " ";

          const valueStr = filter.value ?? "0";
          // Captura texto que NO sean números, puntos, comas, guiones o espacios iniciales/finales
          const textPrefix = valueStr.match(/^[A-Za-z]+/)?.[0] || "";
          const textSuffix = valueStr.match(/[A-Za-z]+$/)?.[0] || "";

          // Para Currency: mostrar símbolo SOLO si no hay prefijo de texto en el valor
          if (filter.kind === DataType.Currency && !textPrefix) {
            friendlyName += CompanyService.GetDefaultCurrencySymbol() + " ";
          }

          // Limpiar el valor para parseo: remover solo las letras, mantener números y separadores
          const cleanValue = valueStr.replace(/[A-Za-z]/g, "").trim();

          // Parsear el número: normalizar coma decimal a punto
          const normalizedValue = cleanValue.replace(",", ".");
          const num = parseFloat(normalizedValue);

          if (!isNaN(num)) {
            const formatter = num % 1 === 0 ? formatInteger : formatNumber;
            if (textPrefix) {
              friendlyName += textPrefix + " ";
            }
            friendlyName += formatter(num);
            if (textSuffix) {
              friendlyName += " " + textSuffix;
            }
          } else {
            // Si no se puede parsear, mostrar el valor original
            friendlyName += valueStr;
          }
        }
        break;
      case DataType.DayCompare:
        friendlyName +=
          TranslationService.getTranslation(
            AdvancedFilterService.numericOperator[filter.operator ?? 0]
          ) + " ";
        friendlyName += parseFloat(filter.value ?? "0");
        break;
      case DataType.Percentage:
        friendlyName +=
          TranslationService.getTranslation(
            AdvancedFilterService.percentageOperators[filter.operator ?? 0]
          ) + " ";
        friendlyName += filter.value + "%";
        break;
      case DataType.Tags:
        friendlyName += filter.value
          ?.replaceAll(" ", "")
          .split(",")
          .map(
            (x) =>
              CompanyService.getTags().find((y) => y.Id === x)?.Value ??
              undefined
          )
          .filter((x) => x !== undefined)
          .join(", ");
        break;
      case DataType.Multiselect:
        {
          const values = filter.value
            ?.split(",")
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
          const labels = filter.definition.AdditionalDefinitionItems.filter(
            (x) => values?.includes(x.Value)
          )
            .map((x) => x.Label)
            .join(", ");
          friendlyName = appendFriendly(friendlyName, labels);
        }
        break;
      case DataType.ActivityType:
        friendlyName += filter.value
          .split(",")
          .map(
            (x) =>
              CompanyService.getActivityTypes().find(
                (y) => y.ActivityTypeID === parseInt(x)
              )?.ActivityTypeName
          )
          .filter((x) => x !== undefined)
          .join(", ");
        if (filter.value === "0") {
          friendlyName += TranslationService.translate.None;
        }
        break;
      case DataType.ClientGroup:
        {
          const clientName: string | undefined = StorageService.getCache(
            CacheEntity.Client,
            filter.value
          );
          friendlyName += clientName;
          if (filter.operator) {
            const group: Group | undefined = StorageService.getCache(
              CacheEntity.GroupName,
              filter.operator
            );
            if (group) {
              friendlyName += group.Name;
            }
          }
        }
        break;
      case DataType.Client:
        friendlyName += clientCache.get(filter.value!);
        break;
      case DataType.Priority:
        friendlyName += (filter.value || "1")
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length > 0)
          .join(", ");
        break;
      case DataType.ExpirationDate:
        {
          filter.value = filter.value === "" ? "all" : filter.value;
          const item =
            AdvancedFilterService.expirationDateOptionsUntranslated.find(
              (x) => x.Value === filter.value
            );
          const agingValues = CompanyService.GetAgeingValues();
          friendlyName += item
            ? TranslationService.getTranslation(item.Label)
            : (agingValues[parseIntOrDefault(filter.value, 412)]?.name ??
              agingValues.find((x) => x.filterVal === filter.value)?.name ??
              "");
        }
        break;
      case DataType.YesNo:
        friendlyName +=
          filter.value === "0"
            ? TranslationService.translate.No
            : TranslationService.translate.Yes;
        break;
      case DataType.User:
        {
          const valueEval = isNullOrWhitespace(filter.value);
          const userSelecet = valueEval
            ? TranslationService.translate.NotSelected
            : filter.definition.AdditionalDefinitionItems.find(
                (x) => x.Value === filter.value
              )?.Label;
          friendlyName += userSelecet;
        }
        break;
      case DataType.UserMultiSelect:
        {
          const values = filter.value
            ?.split(",")
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
          const labels = filter.definition.AdditionalDefinitionItems.filter(
            (x) => values?.includes(x.Value)
          )
            .map((x) => x.Label)
            .join(", ");
          friendlyName = appendFriendly(friendlyName, labels);
        }
        break;
      case DataType.Type:
        friendlyName =
          OptionalMap(
            ActivityService.TypeFilterItems.find(
              (x) => x.Value === filter.value
            )?.Label,
            (x) => friendlyName + TranslationService.getTranslation(x!)
          ) ?? "";
        break;
      case DataType.Status:
        friendlyName += filter.definition.AdditionalDefinitionItems.find(
          (x) => x.Value === (filter.value ?? "0")
        )?.Label;
        break;
      case DataType.ActivityTypeWithDate:
        {
          const activityFilter = filter as FilterActivityTypeWithDate;

          friendlyName += !isNullOrWhitespace(filter.relativeDays)
            ? formatRelativeDaysFilter(filter)
            : filter.value
              ? formatDateFilter(filter)
              : TranslationService.translate.All;
          const activityName = TranslationService.getTranslation(
            ActivityService.ActivityTypeFilterItems.find(
              (x) => x.Value === activityFilter.activityTypeFilter
            )?.Label ?? ""
          );
          friendlyName += isNullOrWhitespace(activityName)
            ? ""
            : " - " + activityName;
          const userName =
            CompanyService.getUsers().find(
              (x) => x.Id === activityFilter.userId
            )?.Value ?? "";
          friendlyName += isNullOrWhitespace(userName) ? "" : " - " + userName;
        }
        break;
    }
    return friendlyName;
  }
  /**
   * Convierte un campo V2 a V1 para compatibilidad al guardar.
   * V1 usa 'personadditional-X' para campos adicionales de clientes; V2 usa 'additional-X'.
   * @param fieldId ID del campo en V2
   * @returns ID del campo en V1 o el mismo si no hay mapeo
   */
  public static mapFieldV2ToV1(
    fieldId: string | undefined
  ): string | undefined {
    if (!fieldId) return fieldId;
    // "additional-X" → "personadditional-X" (campos adicionales de cliente en formato V1)
    if (fieldId.startsWith(V2_ADDITIONAL_PREFIX)) {
      return (
        V1_PERSON_ADDITIONAL_PREFIX +
        fieldId.substring(V2_ADDITIONAL_PREFIX.length)
      );
    }
    return fieldId;
  }

  /**
   * Convierte un campo V1 a V2 para compatibilidad al parsear.
   * V1 usa 'personadditional-X' para campos adicionales de clientes; V2 usa 'additional-X'.
   * @param fieldId ID del campo en V1
   * @returns ID del campo en V2 o el mismo si no hay mapeo
   */
  public static mapFieldV1ToV2(
    fieldId: string | undefined
  ): string | undefined {
    if (!fieldId) return fieldId;
    // "personadditional-X" → "additional-X" (campos adicionales de cliente en formato V1)
    if (fieldId.startsWith(V1_PERSON_ADDITIONAL_PREFIX)) {
      return (
        V2_ADDITIONAL_PREFIX +
        fieldId.substring(V1_PERSON_ADDITIONAL_PREFIX.length)
      );
    }
    return FIELD_MAPPING_V1_TO_V2[fieldId] ?? fieldId;
  }

  /**
   * Convierte un string de filtro de V2 a V1 reemplazando los campos
   * @param filterString String del filtro codificado (query string)
   * @returns String del filtro con campos convertidos a V1
   */
  private static convertFilterStringToV1(filterString: string): string {
    // Parsear el query string para encontrar selFields
    const parts = filterString.split("&");
    const convertedParts = parts.map((part) => {
      const equalIndex = part.indexOf("=");
      if (equalIndex === -1) return part;

      const key = part.substring(0, equalIndex);
      const value = part.substring(equalIndex + 1);

      // Si es selFields, convertir el valor de V2 a V1 solo para campos adicionales de cliente
      if (key === "selFields") {
        try {
          const decodedValue = decodeURIComponent(value);
          if (decodedValue.startsWith(V2_ADDITIONAL_PREFIX)) {
            const additionalId = parseInt(
              decodedValue.substring(V2_ADDITIONAL_PREFIX.length)
            );
            const def = CompanyService.getAdditionalDefinitions().find(
              (d) => d.AdditionalDefinitionID === additionalId
            );
            if (def?.Entity === Entities.Client) {
              return `selFields=${encodeURIComponent(V1_PERSON_ADDITIONAL_PREFIX + decodedValue.substring(V2_ADDITIONAL_PREFIX.length))}`;
            }
          }
        } catch {
          // Si falla, dejar como está
        }
      }

      return part;
    });

    return convertedParts.join("&");
  }

  /**
   * Convierte un array de strings de filtros al formato filter0=...&filter1=...
   * @param filters Array de strings de filtros codificados
   * @param convertToV1 Cuando true, convierte IDs de campos V2 a V1 (ej: "additional-X" → "personadditional-X")
   * @param dateFilterVersion Nombre del parámetro de fecha de Actividad a usar: "v2" (selDate, default) o "v1" (dateFilterActivity)
   * @returns String en formato filter0=...&filter1=... codificado
   */
  public static filterStringsToQueryString(
    filters: string[] | undefined,
    convertToV1: boolean = false,
    dateFilterVersion: "v1" | "v2" = "v2"
  ) {
    if (!filters) return "";

    let processedFilters = convertToV1
      ? filters.map((filter) =>
          AdvancedFilterService.convertFilterStringToV1(filter)
        )
      : filters;

    if (dateFilterVersion === "v1") {
      processedFilters = processedFilters.map((filter) =>
        filter.replace(/(^|&)selDate=/g, "$1dateFilterActivity=")
      );
    }

    return processedFilters
      .map((value, index) => "filter" + index + "=" + encodeURIComponent(value))
      .join("&");
  }
  public static filtersToRequestString(filters: Filter[]) {
    return encodeURIComponent(
      AdvancedFilterService.filterStringsToQueryString(
        filters.map((x) => AdvancedFilterService.filterToString(x))
      ) ?? ""
    );
  }
  public static listOperators: readonly string[] = [
    "EqualTo",
    "IsNot",
    "",
    "",
    "",
    "",
    "Empty",
    "",
    "NotEmpty"
  ];
  /**
   * Operadores disponibles para filtros de tipo List, según la página.
   * "NotEmpty" solo se ofrece como opción en FilterCollection.Task; en el resto de páginas
   * se oculta (se mantiene el índice para no romper el valor numérico de operator ya guardado).
   */
  public static getListOperatorsForPage(
    page?: FilterCollection
  ): readonly string[] {
    if (page === FilterCollection.ReportInvoice) {
      return AdvancedFilterService.listOperators;
    }
    return AdvancedFilterService.listOperators.map((operator, index) =>
      index === AdvancedFilterService.listOperators.indexOf("NotEmpty")
        ? ""
        : operator
    );
  }
  public static numericOperator: readonly string[] = [
    "EqualTo",
    "LessThan",
    "MoreThan",
    "",
    "",
    "IsNot"
  ];
  public static percentageOperators: readonly string[] = [
    "EqualTo",
    "LessThan",
    "MoreThan"
  ];
  public static expirationDateOptionsUntranslated: {
    Label: string;
    Value: string;
  }[] = [
    { Value: "all", Label: "AllClients" },
    { Value: "pending", Label: "PendingCustomersOnly" }
  ];
  public static textWithOperatorOperators: readonly string[] = [
    "Equal",
    "",
    "",
    "Contains",
    "DoesntContain",
    "IsNot",
    "",
    "Empty"
  ];
}

export default AdvancedFilterService;

export interface Filter {
  kind: DataType;
  definition: FilterDefinition;
  value?: string;
  valueid?: string;
  operator?: number;
  /**
   * Relative day count, exactly as typed by the user. Mutually exclusive with `value`:
   * a date filter is either an absolute date/range (`value`) or a relative day count
   * (`relativeDays` + `operator`), never both. Serialized as `dateActivityFilter`.
   */
  relativeDays?: string;
}

export interface FilterActivityTypeWithDate extends Filter {
  activityTypeFilter: string;
  userId: string;
  operatorFilter?: string;
}

type FilterQueryParam = {
  operatorContains?: string;
  text?: string;
  selDate?: string;
  additional?: string;
  amount?: string;
  date?: string;
  personFilter?: string;
  personidFilter?: string;
  selFields?: string;
  status?: string;
  selRank?: string;
  selActivityType?: string;
  operatorList?: string;
  selList?: string;
  selCashflow?: string;
  useridFilter?: string;
  selOperator?: string;
  selOperatorDate?: string;
  tagidsFilter?: string;
  selfilter?: string;
  selYesNo?: string;
  percent?: string;
  selOperatorPercent?: string;
  selDateActivityType?: string;
  selActivityUser?: string;
  activitytypeFilter?: string;
  selStatus?: string;
  typeFilter?: string;
  groupFilterid?: string;
  transactiontype?: string;
  operatortransactiontypelist?: string;
  /**
   * V1 legacy alias of `selDate` (absolute date range in "D/M/YYYY - D/M/YYYY").
   * NOT to be confused with `dateActivityFilter` below — the names are near-identical
   * but the meanings are unrelated.
   */
  dateFilterActivity?: string;
  /** Relative day count entered in the days control. Pairs with `selOperatorDate`. */
  dateActivityFilter?: string;
};

export class FilterLine implements Filter {
  kind: DataType;
  id: string;
  definition: FilterDefinition;
  encodedFilter: string;
  value?: string;
  operator?: number;
  relativeDays?: string;

  constructor() {
    this.id = v4();
    this.kind = -1 as DataType;
    this.definition = {} as FilterDefinition;
    this.encodedFilter = "";
  }

  isEmpty(): boolean {
    return this.kind < 0 && this.encodedFilter === "";
  }

  static From(encodedFilter: string, options: FilterOption[]) {
    // Parsear el query string codificado sin decodificar primero para evitar cortar valores con &
    // Si decodificamos primero, un "&" codificado (%26) se convierte en "&" y el split lo corta incorrectamente
    const item: FilterQueryParam = {};

    // Dividir por "&" en el string codificado (los valores con "&" estarán codificados como %26)
    const parts = encodedFilter.split("&");
    for (const part of parts) {
      // Buscar el primer "=" que separa la clave del valor
      const equalIndex = part.indexOf("=");
      if (equalIndex === -1) continue;

      const key = part.substring(0, equalIndex);
      let value = part.substring(equalIndex + 1);

      // Decodificar solo el valor para manejar caracteres especiales codificados
      try {
        // Decodificar el valor completo (maneja %26, %3D, etc.)
        value = decodeURIComponent(value);
      } catch {
        // Si falla la decodificación, intentar reemplazar + por espacios y decodificar de nuevo
        try {
          value = decodeURIComponent(value.replace(/\+/g, " "));
        } catch {
          // Si aún falla, usar el valor original
        }
      }
      item[key] = value;
    }

    // Helper para decodificar correctamente valores con + y caracteres especiales
    const decodeValue = (value: string | undefined): string | undefined => {
      if (!value) return value;
      try {
        // Reemplazar + por espacios y decodificar caracteres especiales
        return decodeURIComponent(value.replace(/\+/g, " "));
      } catch {
        return value;
      }
    };

    // Mapeo de campos V1 a V2 para compatibilidad al parsear
    // mapFieldV1ToV2 maneja: "personadditional-X" → "additional-X" y "-1008" → "-1007"
    let selFieldsToSearch = item.selFields
      ? (AdvancedFilterService.mapFieldV1ToV2(item.selFields) ?? item.selFields)
      : item.selFields;

    const definition = options.reduce<FilterDefinition | undefined>(
      (prev, cur) => {
        if (prev !== undefined) {
          return prev;
        }

        // Primero intenta buscar por Field ID (usando el campo mapeado si existe)
        let found = cur.definitions.find((y) => y.Field === selFieldsToSearch);

        // Si no encuentra por Field ID, intenta buscar por nombre (para compatibilidad con formato antiguo)
        if (!found && item.selFields) {
          const decodedSelFields = decodeValue(item.selFields);
          found = cur.definitions.find((y) => y.Name === decodedSelFields);
        }

        return found;
      },
      undefined
    );

    // Log para debugging cuando no se encuentra una definición
    if (!definition && item.selFields) {
      const availableFields = options
        .flatMap((opt) => opt.definitions.map((d) => d.Field))
        .join(", ");
      console.warn(
        `Filter definition not found for selFields: "${item.selFields}"${selFieldsToSearch !== item.selFields ? ` (mapped to "${selFieldsToSearch}")` : ""}. Available fields: ${availableFields}`
      );
    }

    // Only remap item.selFields for explicit field ID mappings (e.g. "-1008" → "-1007").
    // Do NOT remap for entity-prefix normalization ("personadditional-X" → "additional-X")
    // so that encodedFilter preserves the original backend prefix.
    if (
      definition &&
      FIELD_MAPPING_V1_TO_V2[item.selFields ?? ""] &&
      item.selFields
    ) {
      item.selFields = AdvancedFilterService.mapFieldV1ToV2(item.selFields);
    }

    try {
      if (item.text) {
        const decodedValue = decodeURIComponent(item.text);
        item.text = decodedValue;
      }
    } catch {}
    const filter = new FilterLine();
    if (definition === undefined) {
      return filter;
    }
    filter.kind = definition.Type;
    filter.definition = definition;

    switch (definition.Type) {
      case DataType.Text:
      case DataType.Phone:
      case DataType.Link:
      case DataType.TextWithOperator:
        filter.value = item.text;
        filter.operator = parseIntOrDefault(item.operatorContains, 0);
        break;
      case DataType.List:
        filter.value =
          item.selList ?? filter.definition.AdditionalDefinitionItems[0].Value;
        // Normalize duplicated operator label for List ("EqualTo" at indexes 0 and 5)
        const parsedOperator = parseIntOrDefault(item.operatorList, 0);
        filter.operator = parsedOperator === 5 ? 0 : parsedOperator;
        break;
      case DataType.ListNoOperator:
        filter.value =
          item.selCashflow ??
          filter.definition.AdditionalDefinitionItems[0].Value;
        break;
      case DataType.Date:
        // `dateActivityFilter` (relative days) wins over any absolute date, mirroring
        // filterToString. Do not confuse it with `dateFilterActivity` (V1 absolute range).
        if (item.dateActivityFilter) {
          filter.relativeDays = item.dateActivityFilter;
          filter.operator = parseIntOrDefault(item.selOperatorDate, 0);
        } else {
          // Usa || (no ??) porque filterToString siempre serializa date="" por defecto;
          // con ?? ese string vacío ganaría y nunca se llegaría al fallback de dateFilterActivity (V1)
          filter.value =
            item.selDate ||
            item.date ||
            (item.dateFilterActivity
              ? convertirFormatoFecha(
                  item.dateFilterActivity.replace(/\+/g, " ")
                )
              : undefined);
          filter.operator = parseIntOrDefault(
            item.selOperator,
            parseIntOrDefault(item.selOperatorDate, 0)
          );
        }
        break;
      case DataType.PeriodoGestiones:
      case DataType.ActivityTypeWithDate:
        {
          const activityFilter =
            filter as unknown as FilterActivityTypeWithDate;
          if (item.dateActivityFilter) {
            filter.relativeDays = item.dateActivityFilter;
            filter.operator = parseIntOrDefault(item.selOperatorDate, 0);
          } else {
            filter.value = item.selDateActivityType;
          }
          activityFilter.activityTypeFilter = item.selActivityType ?? "0";
          activityFilter.userId = item.selActivityUser ?? "0";
        }
        break;
      case DataType.Number:
      case DataType.Currency:
      case DataType.DaysOld:
        filter.value = item.amount;
        filter.operator = parseIntOrDefault(item.selOperator, 0);
        break;
      case DataType.DayCompare:
        // Fallback to item.selDate for filters saved by V1 as Date type
        filter.value = item.date || item.selDate;
        filter.operator = parseIntOrDefault(
          item.selOperatorDate,
          parseIntOrDefault(item.selOperator, 0)
        );
        break;
      case DataType.Percentage:
        filter.value = item.percent;
        filter.operator = parseIntOrDefault(item.selOperatorPercent, 0);
        break;
      case DataType.Tags:
        filter.value = item.tagidsFilter?.replaceAll(",", ", ");
        break;
      case DataType.Multiselect:
        filter.value =
          item.selList ?? filter.definition.AdditionalDefinitionItems[0]?.Value;
        break;
      case DataType.ActivityType:
        filter.value = item.activitytypeFilter?.length
          ? item.activitytypeFilter
          : item.selActivityType;
        break;
      case DataType.Client:
        filter.value = item.personidFilter;
        break;
      case DataType.Priority:
        // selRank fallback keeps compatibility with filters saved as single selection
        filter.value = item.selList || item.selRank || "1";
        break;
      case DataType.ClientGroup:
        filter.value = item.personidFilter;
        filter.operator = OptionalMap(item.groupFilterid, (x) => parseInt(x));
        break;
      case DataType.ExpirationDate:
        filter.value = item.selfilter;
        break;
      case DataType.YesNo:
        filter.value = item.selYesNo;
        break;
      case DataType.User:
        filter.value = item.useridFilter ?? item.selList;
        break;
      case DataType.UserMultiSelect:
        filter.value = item.useridFilter ?? item.selList;
        break;
      case DataType.Type:
        filter.value = item.typeFilter ?? item.selList;
        break;
      case DataType.Status:
        filter.value = item.selStatus ?? "0";
        break;
      default:
        throw Error("Not implemented filter: " + JSON.stringify(filter));
    }

    // Reconstruir el query string desde item para guardarlo en encodedFilter
    // Codificamos los valores para mantener consistencia con filterToString
    const queryParts: string[] = [];
    Object.keys(item).forEach((key) => {
      const value = item[key as keyof FilterQueryParam];
      if (value !== undefined && value !== null) {
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    });
    filter.encodedFilter = queryParts.join("&");
    filter.definition = definition;
    filter.kind = filter.definition.Type;
    return filter;
  }
}

// "Mayor - 30 días". The operator index is a `numericOperator` position, which is what
// the backend already interprets in `selOperatorDate`.
function formatRelativeDaysFilter(filter: Filter) {
  const operatorKey =
    AdvancedFilterService.numericOperator[filter.operator ?? 0];
  const operatorText = operatorKey
    ? TranslationService.getTranslation(operatorKey) + " - "
    : "";
  return `${operatorText}${filter.relativeDays} ${TranslationService.translate.Days}`;
}

function formatDateFilter(filter: Filter) {
  const datesCustom = filter.value && convertirFormatoFecha(filter.value);
  const dates = datesCustom
    ?.split("-")
    .map((x) => formatDateDigits(parseDate(x) ?? new Date()));
  return (
    dates?.join(` ${TranslationService.translate.DateTo} `) ??
    formatDateDigits(new Date())
  );
}

function appendFriendly(firendly: string, value: string | undefined) {
  if (isNullOrWhitespace(value)) {
    return firendly;
  }
  return firendly + " - " + value;
}

function convertirFormatoFecha(cadenaFecha: string) {
  const partes = cadenaFecha.split("-").map((parte) => parte.trim());
  const formatoEntrada = "D/M/YYYY";

  const fechaInicioMoment = moment(partes[0], formatoEntrada, true);
  const fechaFinMoment = moment(partes[1], formatoEntrada, true);

  if (fechaInicioMoment.isValid() && fechaFinMoment.isValid()) {
    const formatoSalida = "YYYYMMDD";
    const fechaInicioFormateada = fechaInicioMoment.format(formatoSalida);
    const fechaFinFormateada = fechaFinMoment.format(formatoSalida);
    return `${fechaInicioFormateada}-${fechaFinFormateada}`;
  } else {
    return cadenaFecha;
  }
}

// function formatRangeDates(cadenaFecha: string) {
//     const nodes = cadenaFecha.split('-');

//     // fechas en formato "dd/mm/yyyy"
//     const startDate = nodes[0].trim();
//     const endDate = nodes[1].trim();

//     const formatedStartDate = formatDateYMD(startDate)
//     const formatedEndDate = formatDateYMD(endDate)
//     return `${formatedStartDate}-${formatedEndDate}`;
// }

// // // Formatea "yyyymmdd"
// function formatDateYMD(date: string) {
//     const dateObj = new Date(`${date.split('/').reverse().join('-')}T00:00:00`);
//     return moment(dateObj.toISOString()).format('YYYYMMDD');
// }

// const formatValidDate = (cadenaFecha: string) => {
//     const fechaMoment = moment(cadenaFecha, 'YYYYMMDD', true);
//     return fechaMoment.isValid() && fechaMoment.format('YYYYMMDD') === cadenaFecha;
// };
